import {
  Box,
  Center,
  Heading,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../../contexts/Auth";
import { buildAuthRedirectPath, getAuthRedirect } from "../authRedirect";
import { Card } from "../components/Login/Card";
import { Link as ChakraLink } from "../components/Login/Link";
import { Logo } from "../components/shared/Logo";
import { SignupForm } from "../components/Signup/SignupForm";
import {
  trackGoogleAdsConversion,
  trackSignUp,
} from "../../../shared/analytics";
import usePageMeta from "../../../shared/hooks/usePageMeta";

export const getSignupErrorMessage = (error) => {
  const validationErrors = error?.response?.data?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const messages = Object.values(validationErrors).flat().filter(Boolean);

    if (messages.length) {
      return messages.join("\n");
    }
  }

  if (error?.response?.status === 409) {
    return "Your email is already registered.";
  }

  return "We couldn't create your account. Please check your details and try again.";
};

const Signup = () => {
  usePageMeta(
    "Sign up for Redwing - free comic reading orders",
    "Create a free Redwing account and get instant access to curated Marvel, DC, Star Wars and Image Comics reading orders with progress tracking.",
    "/signup"
  );
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { signUp } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectPath = getAuthRedirect(location);

  const onLoginSuccess = () => {
    navigate(redirectPath, { replace: true });
  };

  const onSubmit = (values) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    signUp(
      values.firstName,
      values.lastName,
      values.email,
      values.password,
      values.userHandle
    )
      .then(() => {
        trackSignUp("email");
        trackGoogleAdsConversion();
        onLoginSuccess();
      })
      .catch((err) => {
        toast({
          title: "Could not sign up.",
          description: getSignupErrorMessage(err),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Box
      bg={useColorModeValue("gray.50", "inherit")}
      minH="100vh"
      py="12"
      px={{ base: "4", lg: "8" }}
    >
      <Box maxW="md" mx="auto">
        <Center mx="auto" h="8" mb={{ base: "10", md: "20" }}>
          <Logo />
        </Center>
        <Heading textAlign="center" size="xl" fontWeight="extrabold">
          Create an account
        </Heading>
        <Text mt="4" mb="8" align="center" maxW="md" fontWeight="medium">
          <Text as="span">Already have an account?</Text>
          <Link to={buildAuthRedirectPath("/login", redirectPath)}>
            <ChakraLink>Sign in</ChakraLink>
          </Link>
        </Text>
        <Card>
          <SignupForm onSubmit={onSubmit} isSubmitting={isSubmitting} />
        </Card>
        <Text mt="4" mb="8" align="center" maxW="md" fontSize="sm">
          By signing up, you agree to the{" "}
          <Link to="/terms">
            <ChakraLink>Terms of Service</ChakraLink>
          </Link>{" "}
          <br />
          and{" "}
          <Link to="/privacy">
            <ChakraLink>Privacy Policy</ChakraLink>
          </Link>
        </Text>
      </Box>
    </Box>
  );
};

export default Signup;
